import {AbstractLitElement} from "../abstract-lit.element";
import {customElement} from "lit/decorators/custom-element.js";
import {html, TemplateResult} from "lit";
import {Issue} from "../../domain/entities/issue.entity";
import {property} from "lit/decorators/property.js";
import {AppEvent} from "../../events/app-event.interface";
import {IssueTooltipEventHandler} from "../../types/issue-tooltip-event-handler.type";
import {when} from "lit/directives/when.js";
import {map} from "lit/directives/map.js";
import {StringHelper} from "../../helpers/string.helper";
import {translate} from "lit-translate";
import {until} from "lit/directives/until.js";

@customElement(`ha-tooltip`)
export class HaTooltipComponent extends AbstractLitElement {

    private _issueTooltipEventHandler: { handleEvent: IssueTooltipEventHandler } = {
        handleEvent: (evt: AppEvent<{ issue: Issue | null }>) => {
            this.issue = evt.detail.issue;
        }
    }

    private _issue: Issue | null = null;

    @property()
    public get issue(): Issue | null {
        return this._issue;
    }

    public set issue(issue: Issue | null) {
        if (!issue) {
            this.style.display = `none`;
            return;
        }

        this.style.display = `block`;

        if (issue.uid !== this._issue?.uid) {
            const oldIssue = this._issue;
            this._issue = issue;
            this.requestUpdate(`issue`, oldIssue);
        }
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener(`ha-tooltip:start`, this._issueTooltipEventHandler);
        window.addEventListener(`ha-tooltip:stop`, this._issueTooltipEventHandler)
    }

    public render(): TemplateResult {
        if (!this._issue) {
            return html``;
        }

        return html`
            <div class="ha-badge ha-issue__type_description ${this._issue.type.cssClasses}">
                ${this._issue.type.toString()}
            </div>
            ${when(!!this._issue.title && this._issue.title.length > 0, () => html`
                <h6 class="ha-tooltip__header">${this._issue!.title}</h6>
            `)}
            <div class="ha-tooltip__content">
                <table>
                    <tr>
                        <th>Board</th>
                        <td>${this._issue.project?.title ?? ``}</td>
                    </tr>
                    <tr>
                        <th>${translate('general.creator')}</th>
                        <td>${this._issue.creator?.name ?? ``}</td>
                    </tr>
                    <tr>
                        <th>Zeitraum</th>
                        <td>${this._issue.start.toLocal().toLocaleString()} &ndash;
                            ${this._issue.end.minus({day: 1}).toLocal().toLocaleString()}
                        </td>
                    </tr>
                    <tr>
                        <th>Verantwortlich</th>
                        <td>${when(this._issue.employeeToIssueAssignments.length, () => html`
                            <ul class="ha-issue__employees">
                                ${map(this._issue!.employeeToIssueAssignments, assignment => {
                                    return html`
                                        <li>
                                            <div class="employee">
                                                ${until(assignment.employee!.then(employee => employee.name))}
                                            </div>
                                            <div class="timebudget">${assignment.timeBudget}</div>
                                        </li>
                                    `
                                })}
                            </ul>
                        `, () => html`
                            <span class="ha-issue__employees ha-issue__employees_no-assign">nicht zugewiesen</span>
                        `)}
                        </td>
                    </tr>
                </table>

                ${when(
                        StringHelper.hasQuillContent(this._issue.description),
                        () => html`
                            <div class="ha-issue__description">
                                <h7>Beschreibung</h7>
                                <ha-quill-viewer .value="${this._issue!.description}"></ha-quill-viewer>
                            </div>
                        `)}
            </div>
        `;
    }

}
